import { FormEvent, useEffect, useMemo, useState } from "react";
import { Edit, Plus, RefreshCw, Save, Search, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { AdminUser, getCurrentAdmin, logout } from "./api";
import {
  NewsCategory,
  NewsCategoryPayload,
  deleteAdminNewsCategory,
  listAdminNewsCategories,
  saveAdminNewsCategory,
} from "@/services/news";
import { useConfirmDialog } from "./components/ConfirmDialog";
import { AdminSwitchCard } from "./components/AdminChoiceControl";

type FormState = {
  Name: string;
  Description: string;
  IsActive: boolean;
  SortOrder: string;
};

const emptyForm: FormState = {
  Name: "",
  Description: "",
  IsActive: true,
  SortOrder: "100",
};

function categoryToForm(category: NewsCategory): FormState {
  return {
    Name: category.name,
    Description: category.description || "",
    IsActive: category.isActive,
    SortOrder: String(category.sortOrder || 100),
  };
}

export default function AdminNewsCategories() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [editingCategory, setEditingCategory] = useState<NewsCategory | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const filteredCategories = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    if (!normalizedKeyword) return categories;
    return categories.filter((category) =>
      `${category.name} ${category.description || ""}`.toLowerCase().includes(normalizedKeyword),
    );
  }, [categories, keyword]);

  async function load() {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      setCategories(await listAdminNewsCategories());
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "最新消息類別資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function openCreateForm() {
    setEditingCategory(null);
    setForm(emptyForm);
    setMessage("");
    setFormOpen(true);
  }

  function openEditForm(category: NewsCategory) {
    setEditingCategory(category);
    setForm(categoryToForm(category));
    setMessage("");
    setFormOpen(true);
  }

  function closeForm() {
    if (saving) return;
    setFormOpen(false);
    setEditingCategory(null);
    setForm(emptyForm);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.Name.trim()) {
      setMessage("請輸入最新消息類別名稱。");
      return;
    }

    const payload: NewsCategoryPayload = {
      Name: form.Name.trim(),
      Description: form.Description.trim(),
      IsActive: form.IsActive,
      SortOrder: Number(form.SortOrder || 100),
    };

    setSaving(true);
    setMessage("");
    try {
      await saveAdminNewsCategory(editingCategory?.id || null, payload);
      closeForm();
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "最新消息類別儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(category: NewsCategory) {
    const confirmed = await confirm({
      title: "刪除最新消息類別？",
      description: (
        <>
          確定要刪除 <strong className="font-bold text-[#203d32]">{category.name}</strong> 嗎？
          <br />
          如果此類別下已有消息內容，系統會阻擋刪除。
        </>
      ),
      confirmText: "刪除類別",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminNewsCategory(category.id);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "最新消息類別刪除失敗。");
    }
  }

  return (
    <AdminLayout sectionLabel="系統管理" title="最新消息類別管理" user={currentUser} onLogout={handleLogout}>
      <div className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:sticky md:top-[102px] md:z-10 md:flex md:flex-wrap md:items-end md:bg-white/95 md:p-4 md:shadow-[0_10px_24px_rgba(32,61,50,.08)] md:backdrop-blur">
        <div className="grid min-w-0 gap-1 md:min-w-[320px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="news-category-keyword">類別名稱查詢</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7b877f]" size={17} />
            <input
              id="news-category-keyword"
              className="h-11 w-full rounded-md border border-[#cfd8d2] pl-10 pr-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
              placeholder="輸入類別名稱或說明"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
            />
          </div>
        </div>

        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={load} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={openCreateForm} type="button">
          <Plus size={20} />
          新增類別
        </button>
      </div>

      {message && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{message}</div>}

      <section className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="overflow-auto">
          <table className="w-full min-w-[820px] border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f]">
              <tr>
                <th className="px-4 py-3">類別</th>
                <th className="px-4 py-3">狀態</th>
                <th className="px-4 py-3">排序</th>
                <th className="px-4 py-3">更新時間</th>
                <th className="px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={5}>載入中...</td></tr>
              ) : filteredCategories.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={5}>沒有符合條件的最新消息類別。</td></tr>
              ) : filteredCategories.map((category) => (
                <tr className="border-t border-[#edf0ee]" key={category.id}>
                  <td className="px-4 py-4">
                    <strong className="text-[#203d32]">{category.name}</strong>
                    {category.description && <div className="mt-1 max-w-[520px] text-sm text-[#59665f]">{category.description}</div>}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${category.isActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>{category.isActive ? "啟用" : "停用"}</span>
                  </td>
                  <td className="px-4 py-4">{category.sortOrder}</td>
                  <td className="px-4 py-4 text-sm text-[#59665f]">{category.updatedAt || "-"}</td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-2">
                      <button className="inline-flex h-10 items-center gap-2 rounded-md border border-[#cfd8d2] px-3 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => openEditForm(category)} type="button">
                        <Edit size={16} />
                        編輯
                      </button>
                      <button className="inline-flex h-10 items-center gap-2 rounded-md border border-[#f2b8b5] px-3 font-bold text-[#b3261e] hover:bg-[#fdebea]" onClick={() => handleDelete(category)} type="button">
                        <Trash2 size={16} />
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {formOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-[#10251c]/40 px-0 py-0 sm:items-center sm:px-4 sm:py-6">
          <button aria-label="關閉視窗" className="absolute inset-0 cursor-default" onClick={closeForm} type="button" />
          <form className="relative w-full max-w-[560px] rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.2)] sm:rounded-md" onSubmit={handleSubmit}>
            <div className="flex items-start justify-between gap-4 border-b border-[#edf0ee] px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-[#203d32]">{editingCategory ? "編輯最新消息類別" : "新增最新消息類別"}</h2>
                <p className="mt-1 text-sm text-[#7b877f]">類別會用於前台最新消息的側邊選單分類與內容新增/編輯。</p>
              </div>
              <button className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-4 px-5 py-5">
              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>類別名稱 <span className="text-[#c3423f]">*</span></span>
                <input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Name} onChange={(event) => setForm((current) => ({ ...current, Name: event.target.value }))} />
              </label>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                說明
                <textarea className="min-h-[80px] rounded-md border border-[#cfd8d2] px-3 py-2 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Description} onChange={(event) => setForm((current) => ({ ...current, Description: event.target.value }))} />
              </label>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                排序
                <input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="number" value={form.SortOrder} onChange={(event) => setForm((current) => ({ ...current, SortOrder: event.target.value }))} />
              </label>
              <AdminSwitchCard checked={form.IsActive} description="停用後不會出現在前台最新消息側邊選單與內容新增/編輯，但既有內容仍會保留分類。" label="啟用類別" onChange={(IsActive) => setForm((current) => ({ ...current, IsActive }))} />
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                取消
              </button>
              <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} type="submit">
                <Save size={18} />
                {saving ? "儲存中" : "儲存類別"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog />
    </AdminLayout>
  );
}
